import React, { useState, useEffect, useRef } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { getThemeDefaultHighlightColor } from 'components/quote_builder_widget/themes';
import WidgetPreview from 'components/quote_builder_widget/WidgetPreview';
import CssEditorModal from 'components/quote_builder_widget/CssEditorModal';
import SettingsTabs, { tabIdFor, sectionToShow } from 'components/quote_builder_widget/SettingsTabs';
import { ConfigurationSection, CustomerSection, AppearanceSection, MaterialsMenuSection, PrintingSection, CuttingSection, HotStampingSection, ProductImagesSection, InstallationSection } from 'components/quote_builder_widget/sections';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const SAMPLE_TEMPLATE_IDENTIFIER = 'becf-12017';

const getSectionFromHash = () => sectionToShow(window.location.hash.replace('#', '')) || sectionToShow('configuration');

// Sample drawings available for preview, organized by main group
const PREVIEW_DRAWINGS = [
  { value: 'becf-12017', label: 'Cartons | Food Boxes | becf-12017 ' },
  { value: 'becf-10803', label: 'Cartons | Tuck End Boxes | becf-10803' },
  { value: 'becf-21712', label: 'Corrugated Cardboards | Tray Boxes | becf-21712' },
  { value: 'becf-30101', label: 'Hard Cardboards | Covered Solid Board Boxes | becf-30101' },
];

// The drawing the owner last chose to preview, remembered in this browser. Storage can be unavailable (a private window,
// blocked site data), so the preview then starts on the sample drawing.
const PREVIEW_DRAWING_STORAGE_KEY = 'quote_builder_widget_preview_drawing';

const rememberedPreviewDrawing = () => {
  try {
    const remembered = window.localStorage.getItem(PREVIEW_DRAWING_STORAGE_KEY);
    return PREVIEW_DRAWINGS.some((drawing) => drawing.value === remembered) ? remembered : SAMPLE_TEMPLATE_IDENTIFIER;
  } catch (exception) {
    return SAMPLE_TEMPLATE_IDENTIFIER;
  }
};

const rememberPreviewDrawing = (templateIdentifier) => {
  try {
    window.localStorage.setItem(PREVIEW_DRAWING_STORAGE_KEY, templateIdentifier);
  } catch (exception) {
    // Not remembered; the preview still shows the chosen drawing until the page reloads.
  }
};

// Themes that support custom CSS
const THEMES_WITH_CUSTOM_CSS = ['dark', 'classic', 'minimal', 'compact', 'islands', 'bento', 'steps'];

// Map user's default unit to widget unit option
const getDefaultUnitOption = (defaultUnit) => {
  if (defaultUnit === 'in') {
    return 'in';
  }
  return 'mm';
};

const FormNew = ({ identifier, setMode, defaultUnit = 'mm', defaultLanguageOption = 'en', defaultCurrency = 'USD', widgetHost }) => {
  const initialFetchDone = useRef(false);
  const [activeSection, setActiveSection] = useState(getSectionFromHash());
  const [domains, setDomains] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState('classic');
  const [highlightColor, setHighlightColor] = useState(getThemeDefaultHighlightColor('classic'));
  const [submitButtonColor, setSubmitButtonColor] = useState('');
  // Empty: the theme's own background, which a colour would cover (the widget page sets it with !important).
  const [backgroundColor, setBackgroundColor] = useState('');
  const [fontFamily, setFontFamily] = useState('');
  const [unitOption, setUnitOption] = useState(identifier ? 'both' : getDefaultUnitOption(defaultUnit));
  const [languageOption, setLanguageOption] = useState(defaultLanguageOption);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [askCustomerEmail, setAskCustomerEmail] = useState(false);
  const [askCustomerPhone, setAskCustomerPhone] = useState(false);
  // '' (no 2FA), 'sms' or 'email'.
  const [customerTwoFactor, setCustomerTwoFactor] = useState('');
  const [customCss, setCustomCss] = useState('');
  const [customCssByTheme, setCustomCssByTheme] = useState({});
  const [isCssEditorOpen, setIsCssEditorOpen] = useState(false);

  const [addDomainFormVisible, setAddDomainFormVisible] = useState(false);
  const [addDomainFormHighlighted, setAddDomainFormHighlighted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [savedIdentifier, setSavedIdentifier] = useState(identifier);
  const [previewKey, setPreviewKey] = useState(Date.now());
  const [previewDrawing, setPreviewDrawing] = useState(rememberedPreviewDrawing);
  const [setupComplete, setSetupComplete] = useState(false);

  const supportsCustomCss = THEMES_WITH_CUSTOM_CSS.includes(selectedTheme);

  // Send style updates to widget preview iframe for live preview. Only to our own site's frames: another frame on this
  // page (a chat window) must not read the unsaved custom CSS.
  const sendStyleUpdate = (type, data) => {
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      try {
        iframe.contentWindow.postMessage({ type, ...data }, window.location.origin);
      } catch (error) {
        // Ignore cross-origin errors
      }
    });
  };

  const appearanceUpdates = {
    theme: ['diecuttemplates.update_theme', { theme: selectedTheme }],
    customCss: ['diecuttemplates.update_css', { css: customCss }],
    backgroundColor: ['diecuttemplates.update_background_color', { color: backgroundColor }],
    fontFamily: ['diecuttemplates.update_font_family', { fontFamily: fontFamily }],
    highlightColor: ['diecuttemplates.update_highlight_color', { color: highlightColor }],
    submitButtonColor: ['diecuttemplates.update_submit_button_color', { color: submitButtonColor || highlightColor }],
  };

  useEffect(() => {
    if (savedIdentifier) {
      sendStyleUpdate(...appearanceUpdates.theme);
    }
  }, [selectedTheme, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate(...appearanceUpdates.customCss);
    }
  }, [customCss, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate(...appearanceUpdates.backgroundColor);
    }
  }, [backgroundColor, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate(...appearanceUpdates.fontFamily);
    }
  }, [fontFamily, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate(...appearanceUpdates.highlightColor);
    }
  }, [highlightColor, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate(...appearanceUpdates.submitButtonColor);
    }
  }, [submitButtonColor, highlightColor, supportsCustomCss, savedIdentifier]);

  // A preview that loads again (another drawing, a Materials change) renders the saved appearance; once it says it is
  // listening, it is sent the appearance on this page, saved or not.
  useEffect(() => {
    const onPreviewReady = (event) => {
      if (event.origin !== window.location.origin || !event.data || event.data.type !== 'diecuttemplates.preview_ready' || !event.source) {
        return;
      }
      Object.values(appearanceUpdates).forEach(([type, data]) => event.source.postMessage({ type, ...data }, event.origin));
    };
    window.addEventListener('message', onPreviewReady);
    return () => window.removeEventListener('message', onPreviewReady);
  }, [selectedTheme, customCss, backgroundColor, fontFamily, highlightColor, submitButtonColor]);

  // A highlight colour left at the theme's default follows the new theme; one the owner chose stays. Custom CSS is kept
  // for each theme. A background colour does not carry over: one that suits a light theme hides a dark theme's light
  // text (and the other way round), so the new theme starts on its own background.
  const handleThemeChange = (theme) => {
    if (highlightColor === getThemeDefaultHighlightColor(selectedTheme)) {
      setHighlightColor(getThemeDefaultHighlightColor(theme));
    }
    setCustomCssByTheme((previous) => ({ ...previous, [selectedTheme]: customCss }));
    setCustomCss(customCssByTheme[theme] || '');
    setBackgroundColor('');
    setSelectedTheme(theme);
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    window.history.pushState(null, '', `#${section}`);
  };

  useEffect(() => {
    const onPopState = () => {
      setActiveSection(getSectionFromHash());
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // The preview shows only once the widget can price a quote (QuoteBuilderWidget::SetupStatus). Read again whenever the
  // preview would reload -- every change in the Materials, Printing and Cutting tabs reloads it.
  useEffect(() => {
    if (!savedIdentifier) {
      return;
    }
    GetRequest({
      url: '/quote-builder/widgets/setup-status',
      callback: (data) => { setSetupComplete(data.complete); },
    });
  }, [savedIdentifier, previewKey]);

  const fetchWidget = () => {
    if (identifier === undefined) {
      return;
    }

    GetRequest({
      url: `/quote-builder/user-quote-builder-widgets/${identifier}`,
      callback: (data) => {
        const theme = data.properties.theme || 'classic';
        setDomains(data.domains);
        setSelectedTheme(theme);
        setHighlightColor(data.properties.highlight_color || getThemeDefaultHighlightColor(theme));
        setSubmitButtonColor(data.properties.submit_button_color || '');
        setBackgroundColor(data.properties.background_color || '');
        setFontFamily(data.properties.font_family || '');
        setUnitOption(data.properties.unit_option || 'both');
        setLanguageOption(data.language_option);
        setCurrency(data.currency);
        setAskCustomerEmail(data.properties.ask_customer_email === true);
        setAskCustomerPhone(data.properties.ask_customer_phone === true);
        setCustomerTwoFactor(data.properties.customer_two_factor || '');
        setCustomCss(data.custom_css || '');
        setCustomCssByTheme(data.custom_css_by_theme || {});
        setTimeout(() => {
          initialFetchDone.current = true;
        }, 100);
      }
    });
  };

  const handleCancel = (event) => {
    event.preventDefault();
    setMode();
  };

  const saveWidget = (overrides = {}) => {
    const domainsToSave = overrides.domains !== undefined ? overrides.domains : domains;

    const saveData = {
      identifier: savedIdentifier,
      domains: domainsToSave,
      theme: selectedTheme,
    };

    if (supportsCustomCss) {
      saveData.highlight_color = highlightColor;
      saveData.submit_button_color = submitButtonColor;
      saveData.background_color = backgroundColor;
      saveData.font_family = fontFamily;
      saveData.custom_css = overrides.customCss !== undefined ? overrides.customCss : customCss;
    }

    saveData.unit_option = unitOption;
    saveData.language_option = languageOption;
    saveData.currency = currency;
    saveData.ask_customer_email = askCustomerEmail;
    saveData.ask_customer_phone = askCustomerPhone;
    saveData.customer_two_factor = customerTwoFactor;

    PostRequest({
      url: '/quote-builder/user-quote-builder-widgets',
      data: saveData,
      callback: (data) => {
        setSuccessMessage(t('quote_builder_widget.saved_successfully'));
        setPreviewKey(Date.now());
        if (!savedIdentifier) {
          setSavedIdentifier(data.identifier);
          window.history.replaceState({}, '', `/quote-builder/widgets/${data.identifier}/edit`);
        }
      },
      400: (data) => {
        setErrorMessage(data.error);
      }
    });
  };

  // Auto-save when domains change (after initial fetch)
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    if (domains.length === 0) {
      return;
    }
    saveWidget();
  }, [domains]);

  const handleSave = (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (domains.length === 0) {
      handleSectionChange('configuration_domains');
      setAddDomainFormVisible(true);
      setAddDomainFormHighlighted(true);
      return;
    }

    saveWidget();
  };

  useEffect(fetchWidget, [identifier]);

  const renderActiveSection = () => {
    switch (tabIdFor(activeSection)) {
      case 'configuration':
        return (
          <ConfigurationSection
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            domains={domains}
            setDomains={setDomains}
            unitOption={unitOption}
            setUnitOption={setUnitOption}
            languageOption={languageOption}
            setLanguageOption={setLanguageOption}
            currency={currency}
            setCurrency={setCurrency}
            addDomainFormVisible={addDomainFormVisible}
            setAddDomainFormVisible={setAddDomainFormVisible}
            addDomainFormHighlighted={addDomainFormHighlighted}
            setAddDomainFormHighlighted={setAddDomainFormHighlighted}
            newDomain={newDomain}
            setNewDomain={setNewDomain}
          />
        );
      case 'customer':
        return (
          <CustomerSection
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            askCustomerEmail={askCustomerEmail}
            setAskCustomerEmail={setAskCustomerEmail}
            askCustomerPhone={askCustomerPhone}
            setAskCustomerPhone={setAskCustomerPhone}
            customerTwoFactor={customerTwoFactor}
            setCustomerTwoFactor={setCustomerTwoFactor}
          />
        );
      case 'appearance':
        return (
          <AppearanceSection
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            selectedTheme={selectedTheme}
            setSelectedTheme={handleThemeChange}
            highlightColor={highlightColor}
            setHighlightColor={setHighlightColor}
            submitButtonColor={submitButtonColor}
            setSubmitButtonColor={setSubmitButtonColor}
            backgroundColor={backgroundColor}
            setBackgroundColor={setBackgroundColor}
            fontFamily={fontFamily}
            setFontFamily={setFontFamily}
            customCss={customCss}
            supportsCustomCss={supportsCustomCss}
            onOpenCssEditor={() => setIsCssEditorOpen(true)}
          />
        );
      case 'materials':
        return (
          <MaterialsMenuSection
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            onMaterialsChange={() => setPreviewKey(Date.now())}
          />
        );
      case 'printing':
        return (
          <PrintingSection
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            onChange={() => setPreviewKey(Date.now())}
          />
        );
      case 'cutting':
        return (
          <CuttingSection
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            onChange={() => setPreviewKey(Date.now())}
          />
        );
      case 'hot_stamping':
        return (
          <HotStampingSection
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            onChange={() => setPreviewKey(Date.now())}
          />
        );
      case 'product_images':
        return (
          <ProductImagesSection
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            onChange={() => setPreviewKey(Date.now())}
          />
        );
      case 'installation':
        return (
          <InstallationSection
            activeSection={activeSection}
            widgetHost={widgetHost}
            identifier={savedIdentifier}
            domains={domains}
            onSectionChange={handleSectionChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Error Message */}
      {errorMessage !== '' && (
        <div className={`alert alert-danger fade in ${styles.errorAlert}`}>
          <strong><i className="fa fa-exclamation-circle"></i> {errorMessage}</strong>
        </div>
      )}

      {/* Main Layout */}
      <div className={styles.pageWrapper}>
        <a href="/quote-builder/widgets" className={styles.backLink}>
          <i className="fa fa-arrow-left"></i> {t('quote_builder_widget.back')}
        </a>
        <div className={styles.header}>
          <SettingsTabs
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
          />
        </div>

        {/* Content + Preview */}
        <div className={styles.container}>
          {/* Content Area */}
          <div
            className={styles.contentPanel}
            role="tabpanel"
            id={`tabpanel-${tabIdFor(activeSection)}`}
            aria-labelledby={`tab-${tabIdFor(activeSection)}`}
          >
            {renderActiveSection()}
            {!['materials', 'printing', 'cutting', 'hot_stamping', 'product_images', 'installation'].includes(tabIdFor(activeSection)) && (
              <div className={styles.footerActions}>
                {successMessage !== '' && (
                  <span className={styles.savedMessage}><i className="fa fa-check-circle"></i> {successMessage}</span>
                )}
                <button className="dct-button dct-button--primary" type="button" onClick={handleSave}>
                  {t('quote_builder_widget.save')}
                </button>
                <button className="dct-button dct-button--secondary" type="button" onClick={handleCancel}>
                  {t('quote_builder_widget.cancel')}
                </button>
              </div>
            )}
          </div>

          {/* Preview Panel */}
          {savedIdentifier && setupComplete && (
            <div className={styles.previewContainer}>
              <center>
                <h5 className={styles.previewTitle}>{t('quote_builder_widget.preview')}</h5>
                <div className={styles.previewDropdownContainer}>
                  <select
                    className={`form-control ${styles.previewSelect}`}
                    aria-label={t('quote_builder_widget.preview_drawing')}
                    value={previewDrawing}
                    onChange={(event) => {
                      setPreviewDrawing(event.target.value);
                      rememberPreviewDrawing(event.target.value);
                      setPreviewKey(Date.now());
                    }}
                  >
                    {PREVIEW_DRAWINGS.map((drawing) => (
                      <option key={drawing.value} value={drawing.value}>
                        {drawing.label}
                      </option>
                    ))}
                  </select>
                </div>
                <hr className={styles.previewSeparator} />
                <div className={styles.previewBox}>
                  <WidgetPreview
                    identifier={savedIdentifier}
                    templateIdentifier={previewDrawing}
                    previewKey={previewKey}
                  />
                </div>
              </center>
            </div>
          )}
        </div>
      </div>

      {supportsCustomCss && (
        <CssEditorModal
          isOpen={isCssEditorOpen}
          onClose={() => setIsCssEditorOpen(false)}
          onSave={(newCss) => {
            setCustomCss(newCss);
            setCustomCssByTheme((previous) => ({
              ...previous,
              [selectedTheme]: newCss
            }));
            setIsCssEditorOpen(false);
            saveWidget({ customCss: newCss });
          }}
          currentCss={customCss}
          theme={selectedTheme}
          domains={domains}
        />
      )}
    </div>
  );
};

export default FormNew;
